import { Autocomplete, TextField, TextFieldProps } from '@mui/material';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  get,
} from 'react-hook-form';

/**
 * RHF-aware Autocomplete that filters as the user types.
 *
 * Behaves like a free-text field that snaps to options when one matches.
 * `freeSolo` is enabled because real cities/districts can lag behind the
 * canonical list and we'd rather accept a slightly-off value than block
 * the user from registering.
 */
type Props<T extends FieldValues> = Omit<
  TextFieldProps,
  'name' | 'error' | 'value' | 'onChange'
> & {
  name: Path<T>;
  control: Control<T>;
  errors: FieldErrors<T>;
  options: readonly string[];
  /** Disable the dropdown until a parent value is chosen (e.g. state for districts). */
  disabled?: boolean;
};

export function AutocompleteField<T extends FieldValues>({
  name,
  control,
  errors,
  options,
  helperText,
  fullWidth = true,
  label,
  disabled = false,
  ...rest
}: Props<T>) {
  const fieldError = get(errors, name) as { message?: string } | undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          freeSolo
          autoHighlight
          openOnFocus
          disabled={disabled}
          options={options as string[]}
          value={(field.value as string) || ''}
          onChange={(_, newValue) => field.onChange(newValue ?? '')}
          onInputChange={(_, newInput) => field.onChange(newInput)}
          isOptionEqualToValue={(opt, val) =>
            opt.toLowerCase() === (val ?? '').toLowerCase()
          }
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            if (!q) return opts.slice(0, 50);
            return opts
              .filter((o) => o.toLowerCase().includes(q))
              .slice(0, 50);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              {...rest}
              label={label}
              fullWidth={fullWidth}
              error={!!fieldError}
              helperText={fieldError?.message ?? helperText}
              onBlur={field.onBlur}
              inputRef={field.ref}
            />
          )}
        />
      )}
    />
  );
}
